// HubSpot global types are declared in lib.hooks/src/sendAnalytics/types.ts

export const isHubSpotScriptLoaded = () => !!window.HubSpotConversations;

export const toggleHubspotStyles = (isOpen: boolean) => {
  const hubspotContainer = document.querySelector('#hubspot-messages-iframe-container');
  if (hubspotContainer) {
    if (isOpen) {
      hubspotContainer.classList.remove('visible');
    } else {
      hubspotContainer.classList.add('visible');
    }
  }
};

export const toggleHubspotChat = (isOpen: boolean) => {
  if (!window.HubSpotConversations) {
    return;
  }
  toggleHubspotStyles(isOpen);
  if (isOpen) {
    window.HubSpotConversations.widget.close();
  } else {
    window.HubSpotConversations.widget.open();
  }
};

export const isMobileDevice = () => {
  const uaData = (navigator as any)?.userAgentData;
  if (uaData && typeof uaData.mobile === 'boolean') {
    return uaData.mobile;
  }
  return /Mobi|Android/i.test(navigator.userAgent);
};

export interface HubSpotIdentityResponse {
  email: string;
  token: string;
  first_name: string;
  last_name: string;
  timestamp: number;
}

let lastIdentifiedEmail: string | null = null;
let isWidgetLoaded = false;

export const fetchHubspotIdentity = async (): Promise<HubSpotIdentityResponse | null> => {
  try {
    const response = await fetch('/api/hubspot/identity', {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to fetch HubSpot identity:', error);
    return null;
  }
};

const loadWidget = () => {
  if (window.HubSpotConversations && !isWidgetLoaded) {
    window.HubSpotConversations.widget.load();
    isWidgetLoaded = true;
  }
};

const refreshWidget = () => {
  if (window.HubSpotConversations) {
    window.HubSpotConversations.widget.refresh();
  }
};

export const identifyAndLoadHubspotWidget = (identity: HubSpotIdentityResponse) => {
  if (!identity.email || !identity.token) {
    console.error('HubSpot identity requires email and token');
    return;
  }

  // Prevent duplicate identify calls for the same user
  if (lastIdentifiedEmail === identity.email && isWidgetLoaded) {
    return;
  }

  // Set identification credentials BEFORE loading widget (per HubSpot docs)
  window.hsConversationsSettings = {
    ...window.hsConversationsSettings,
    loadImmediately: false,
    identificationEmail: identity.email,
    identificationToken: identity.token,
  };

  lastIdentifiedEmail = identity.email;

  // Use hsConversationsOnReady to handle case when API is not yet available
  if (window.HubSpotConversations) {
    if (isWidgetLoaded) {
      refreshWidget();
    } else {
      loadWidget();
    }
  } else {
    // API not ready yet - register callback
    window.hsConversationsOnReady = window.hsConversationsOnReady || [];
    window.hsConversationsOnReady.push(loadWidget);
  }
};

export const loadHubspotWidgetAnonymous = () => {
  // Clear any previous identification (important for logout flow)
  if (window.hsConversationsSettings) {
    delete window.hsConversationsSettings.identificationEmail;
    delete window.hsConversationsSettings.identificationToken;
  }
  lastIdentifiedEmail = null;

  if (window.HubSpotConversations) {
    if (isWidgetLoaded) {
      // Widget already loaded - refresh to apply anonymous state
      refreshWidget();
    } else {
      loadWidget();
    }
  } else {
    // API not ready yet - register callback
    window.hsConversationsOnReady = window.hsConversationsOnReady || [];
    window.hsConversationsOnReady.push(loadWidget);
  }
};

export const clearHubspotIdentity = () => {
  lastIdentifiedEmail = null;
  isWidgetLoaded = false;
  if (window.hsConversationsSettings) {
    delete window.hsConversationsSettings.identificationEmail;
    delete window.hsConversationsSettings.identificationToken;
  }
};
